import _ from "lodash";
import submit from "submit-form";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import PendingInvoiceModal from "../../../parts/modals/pending-invoice";
import {
  mergeConfigsByClientIdCache,
  pickConfigsForClientIds,
  readConfigsByClientIdCache,
  rememberBrowserSession,
} from "../../../utils/configs-by-client-id-cache";
import {
  canViewAllOrganizationClients,
  hasOwnAssignedClientsOnly,
} from "../../../utils/organization-permissions-helpers";
import { hasLoaderCache } from "../../../utils/rill-loader-cache";
import {
  filterAthletesAttrs,
  getUserTimeZone,
  parseAthletesOrganizationInfoToClientList,
  parseAthletesOrganizationInfoToGroups,
} from "../../../utils/user-helpers";
import Athletes from "./Athletes";
import { AcknowledgeAthleteModal } from "./components/AcknowledgeAthleteModal";
import GroupModal from "./components/GroupModal";
import InviteAthleteModal from "./components/InviteAthleteModal";
import ManageGroupModal from "./components/ManageGroupModal";
import TestAccountModal from "./components/TestAccountModal";

/**
 * Which athletes the client list shows, in priority order: an explicit group filter, an
 * explicitly selected coach, a Team Coach's org-wide default (they never have their own
 * roster to fall back to), and finally the active user's own athletes.
 */
const resolveClientListAthletes = ({
  filterGroup,
  allAthletesFromOrganization,
  coachOrganizationInformation,
  isTeamCoach,
  activeUser,
  pendingAthleteInvitations,
}) => {
  if (filterGroup && filterGroup !== "all" && allAthletesFromOrganization) {
    return parseAthletesOrganizationInfoToClientList(
      allAthletesFromOrganization
    );
  }
  if (coachOrganizationInformation) {
    return [
      ...coachOrganizationInformation.athletes,
      ...pendingAthleteInvitations,
    ];
  }
  if (isTeamCoach && allAthletesFromOrganization) {
    return parseAthletesOrganizationInfoToClientList(
      allAthletesFromOrganization
    );
  }
  return [...activeUser.athletes, ...pendingAthleteInvitations];
};

// Computing a config for every client in a large, cold list is what makes
// this page slow for big organizations (each unconfigured client costs a
// real DB round-trip). Past this size, if the relevant scope isn't already
// cached, we fetch only the first page synchronously and let the browser
// fetch configs for whichever rows are on screen (see AthletesConfigList.tsx).
const CONFIGS_PAGE_SIZE = 30;

export const getRoot = async (ctx) => {
  const { res, req, locals, load, flash, session } = ctx;
  const { query = {} } = req;
  const {
    email = "",
    modal = {},
    sort = JSON.stringify([["name"], ["asc"]]),
    filterGroup = null,
    organizationCoach = null,
  } = query;

  // This warning flash error will be triggered when trying to access the store
  // from a responsive screen because it is now designed yet
  // check Store.js componentDidMount(){}
  if (req.query?.flashError) {
    flash("warning", req.query.flashError);
    return submit("/actions/refresh", {
      body: {
        flashError: "",
      },
    });
  }

  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsAthletes = canViewAllOrganizationClients(
    locals.organizationRoleName
  );

  // A Team Coach never has clients of their own, so the "no coach/group picked
  // yet" default below can't fall back to activeUser.athletes like it does for
  // every other role — that's always empty for them. Default straight to the
  // org-wide list instead.
  const isTeamCoach = locals.organizationRoleName === "Team Coach";

  const isAllowedToViewAllUnacknowledgedAthletes =
    isAllowToViewAllOrganizationsAthletes ||
    hasOwnAssignedClientsOnly(locals.organizationRoleName);

  const loadPromises = [
    load("pendingAthleteInvitations", {
      user: organizationCoach || locals.activeUser._id,
    }),
  ];

  if (organizationCoach) {
    loadPromises.push(
      load("coachOrganizationInformation", { user: organizationCoach })
    );
  }

  if (locals.activeUser.organizationId) {
    loadPromises.push(load("usersOrganization"));
  }

  if (email) {
    loadPromises.push(load("userByEmail", { email: _.toLower(email) }));
  } else {
    locals.userByEmail = undefined;
  }

  if (
    locals.activeUser.organizationId &&
    isAllowedToViewAllUnacknowledgedAthletes
    //     && !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  if (modal.type === "pending-invoice") {
    loadPromises.push(load("pendingInvoices"));
  }

  await Promise.all(loadPromises);

  let allAthletes = resolveClientListAthletes({
    filterGroup,
    allAthletesFromOrganization: locals.allAthletesFromOrganization,
    coachOrganizationInformation: locals.coachOrganizationInformation,
    isTeamCoach,
    activeUser: locals.activeUser,
    pendingAthleteInvitations: locals.pendingAthleteInvitations,
  });

  const athletesForModalLookup = locals.allAthletesFromOrganization
    ? parseAthletesOrganizationInfoToClientList(
        locals.allAthletesFromOrganization
      )
    : locals.activeUser.athletes;

  if (filterGroup && filterGroup !== "all" && filterGroup !== "organization") {
    allAthletes = allAthletes.filter((athlete) =>
      athlete.groups?.includes(filterGroup)
    );
  }

  locals.athletes = _.orderBy(allAthletes, ...JSON.parse(sort));

  //filter by name and email using regexp
  if (query.name) {
    locals.athletes = locals.athletes.filter((athlete) => {
      return (
        new RegExp(`^.*${query.name}.*$`, "gi").test(athlete.name) ||
        new RegExp(`^.*${query.name}.*$`, "gi").test(athlete.email)
      );
    });
  }

  if (query.showConfigList) {
    // Lets the list cache what it lazy-loads in the browser's session copy
    // too (see configs-by-client-id-cache.js).
    rememberBrowserSession(session);
    const clientIds = locals.athletes
      .map((athlete) => athlete._id)
      .filter(Boolean)
      .join(",");

    const isSectionBlocksCached =
      clientIds &&
      hasLoaderCache(session, "sectionBlocksByClientId", { clientIds });

    locals.sectionBlocksByClientId = {};
    locals.prefetchClientIds = isSectionBlocksCached ? null : clientIds;

    // Configs accumulate in one session cache shared by every view (see
    // configs-by-client-id-cache.js), so clients already loaded in another
    // view — e.g. "All clients" before switching to a group — aren't fetched
    // again. Warmth is coverage of *this* athlete list, not merely that the
    // cache exists.
    const athleteIdList = locals.athletes
      .map((athlete) => String(athlete._id))
      .filter(Boolean);
    const cachedMap = readConfigsByClientIdCache(session);
    const missingIds = athleteIdList.filter((id) => !cachedMap[id]);
    // Only the server render (first page load) fetches a first page up
    // front. In-app navigations — switching group / viewed coach — run this
    // controller in the browser; there the page renders right away and the
    // table lazy-loads whichever rows are on screen instead.
    const ssrBatch = process.browser
      ? []
      : missingIds.slice(0, CONFIGS_PAGE_SIZE);

    const loads = [];
    if (ssrBatch.length) {
      loads.push(
        load("configsByClientId", {
          filterGroup,
          organizationCoach,
          clientIds: ssrBatch,
        })
      );
    }
    if (clientIds && isSectionBlocksCached)
      loads.push(load("sectionBlocksByClientId", { clientIds }));
    const results = await Promise.all(loads);
    const fetchedMap = ssrBatch.length ? (results[0] ?? {}) : {};

    if (Object.keys(fetchedMap).length) {
      mergeConfigsByClientIdCache(session, fetchedMap);
    }

    const configsMap = { ...cachedMap, ...fetchedMap };
    locals.configsByClientId = pickConfigsForClientIds(
      configsMap,
      athleteIdList
    );
    locals.configsPagination = athleteIdList.some((id) => !configsMap[id])
      ? { pageSize: CONFIGS_PAGE_SIZE, filterGroup, organizationCoach }
      : null;

    await load("clientConfigWidgetAvailability");
  }

  locals.modal = {
    "invite-athlete": (
      <InviteAthleteModal existingUser={locals.userByEmail} email={email} />
    ),
    "new-group": (
      <ManageGroupModal
        locals={locals}
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    "members-group": (
      <ManageGroupModal
        locals={locals}
        originalGroupName={query.filterGroup}
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    "acknowledge-athlete": (
      <AcknowledgeAthleteModal
        locals={locals}
        athlete={athletesForModalLookup?.find(
          (a) => String(a._id) === req?.query?.acknowledgeAthlete
        )}
      />
    ),
    "update-group": (
      <GroupModal
        originalGroupName={query.filterGroup}
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    "test-account": <TestAccountModal />,
    "activity-feed": <ActivityDrawer />,
    "pending-invoice": <PendingInvoiceModal />,
  }[modal.type];

  locals.query = query;
  res.body = <Athletes />;
};

export const postInviteAthlete = async ({ res, req, locals, flash, api }) => {
  const { body } = req;
  const referAsClients = Boolean(locals.activeUser.referAsClients);
  const email = _.get(body, "email", "");
  if (email === locals.activeUser.email) {
    flash("error", "You can't invite yourself");
    res.refreshWith(
      { modal: "", email: "" },
      { url: "/coaching-portal/athletes", hash: "#_" }
    );
  }

  if (email && email !== locals.activeUser.email) {
    await api("invite-athlete", { method: "POST", body }).then((response) => {
      if (response.status === 200) {
        flash(
          "success",
          referAsClients ? "Client invitation sent" : "Athlete invitation sent"
        );
        res.refreshWith(
          { modal: "", email: "" },
          { url: "/coaching-portal/athletes", hash: "#_" }
        );
      } else {
        flash(
          "error",
          response.statusText || "There was an error sending the invitation"
        );
        res.refreshWith(
          { modal: "", email: "" },
          { url: "/coaching-portal/athletes", hash: "#_" }
        );
      }
    });
  }
};

export const postCancelInvite = async ({ res, req, flash, api }) => {
  const { body } = req;
  const email = _.get(body, "email", "");

  if (email) {
    try {
      const result = await (
        await api("cancel-coaching-invite", {
          method: "POST",
          body,
        })
      ).json();
      if (result.success) {
        flash("success", "Invitation canceled");
        const redirectTo = body.organizationCoachId
          ? {
              url: `/coaching-portal/athletes?organizationCoach=${body.organizationCoachId}`,
              hash: "#_",
            }
          : { url: "/coaching-portal/athletes", hash: "#_" };
        res.refreshWith({}, redirectTo);
      } else {
        throw {
          message: "There is a problem when trying to cancel the invitation",
        };
      }
    } catch (e) {
      console.log("error", e);
      flash("error", e.message);
      res.refreshWith({}, { url: "back" });
    }
  }
};

export const postResendInvite = async ({ res, req, flash, api }) => {
  const { body } = req;
  const email = _.get(body, "email", "");

  if (email) {
    try {
      await api("resend-code", {
        method: "POST",
        body,
      });
      flash("success", "Invitation re-sent");
      res.refreshWith({}, { url: "/coaching-portal/athletes", hash: "#_" });
    } catch (e) {
      flash("error", e.message);
      res.refreshWith({}, { url: "/coaching-portal/athletes", hash: "#_" });
    }
  }
};

export const postNewGroup = async ({
  res,
  req,
  locals,
  flash,
  api,
  load,
  assert,
}) => {
  const { body } = req;
  const { activeUser, organizationOwner, organizationRoleName } = locals;
  const { name, description, selected } = body;

  assert(name, 400, "Please fill out the name field");

  // A Team Coach has no roster of their own, so a group they create belongs to
  // the Owner instead (same stand-in used for invites and Acknowledge's coach
  // picker) — and the selected athletes can come from any coach in the org.
  const isTeamCoach = organizationRoleName === "Team Coach";
  if (isTeamCoach && organizationOwner) {
    const athletesGroups = organizationOwner.athletesGroups || [];
    assert(
      !athletesGroups.some((elem) => elem.name === name),
      400,
      "Groups names must be different"
    );
    athletesGroups.push({ name, description });

    if (!locals.allAthletesFromOrganization) {
      await load("allAthletesFromOrganization");
    }
    const allAthletes = parseAthletesOrganizationInfoToGroups(
      locals.allAthletesFromOrganization
    );

    // Group the org's athletes by their current coach, same sweep deleteGroup
    // uses, so each coach's own athletes get PUT once.
    const coachIdMap = {};
    allAthletes.forEach((athlete) => {
      const coachId = athlete.coachId;
      if (!coachIdMap[coachId]) coachIdMap[coachId] = [];
      coachIdMap[coachId].push(athlete);
    });

    const userUpdatePromises = [];
    for (const coachId in coachIdMap) {
      const updatedAthletes = coachIdMap[coachId].map((athlete) => {
        const updatedAthlete = athlete;
        const found = selected
          ? selected.find((elem) => elem._id === updatedAthlete._id)
          : null;
        if (found && !updatedAthlete.groups?.includes(name)) {
          const groups = updatedAthlete.groups || [];
          updatedAthlete.groups = [...groups, name];
        }
        return filterAthletesAttrs(updatedAthlete);
      });

      userUpdatePromises.push(
        String(organizationOwner._id) === String(coachId)
          ? api(`user/${coachId}`, {
              method: "PUT",
              body: { athletesGroups, athletes: updatedAthletes },
            })
          : api(`user/${coachId}`, {
              method: "PUT",
              body: { athletes: updatedAthletes },
            })
      );
    }

    await Promise.all(userUpdatePromises);
    flash("success", "Group created");
    res.refreshWith(
      { modal: "" },
      { url: `/coaching-portal/athletes/?filterGroup=${name}`, hash: "#_" }
    );
    return;
  }

  const { athletes } = activeUser;
  const athletesGroups = activeUser.athletesGroups || [];

  assert(
    !athletesGroups.some((elem) => elem.name === name),
    400,
    "Groups names must be different"
  );

  athletesGroups.push({
    name: name,
    description: description,
  });
  const newAthletes = athletes.map((athlete) => {
    const updatedAthlete = athlete;
    const found = selected
      ? selected.find((elem) => elem._id === updatedAthlete._id)
      : null;

    if (found) {
      if (!found.groups?.includes(name)) {
        const groups = found.groups || [];
        updatedAthlete.groups = [...groups, name];
      }
    } else {
      // delete string if necessary
      if (updatedAthlete.groups) {
        updatedAthlete.groups = athlete.groups.filter((e) => e !== name);
      }
    }

    return filterAthletesAttrs(updatedAthlete);
  });
  await api(`user/${activeUser._id}`, {
    method: "PUT",
    body: { athletesGroups, athletes: newAthletes },
  });
  flash("success", "Group created");

  await load("activeUser", { user: locals.activeUser._id });
  res.refreshWith(
    { modal: "" },
    { url: `/coaching-portal/athletes/?filterGroup=${body.name}`, hash: "#_" }
  );
};

export const postSortGroups = async ({
  res,
  req,
  locals,
  flash,
  api,
  assert,
}) => {
  const { activeUser, organizationOwner, organizationRoleName } = locals;

  // A Team Coach has no groups of their own — the groups being sorted live on
  // the Owner instead (same stand-in used elsewhere for a Team Coach).
  const isTeamCoach = organizationRoleName === "Team Coach";
  const groupsOwnerId = isTeamCoach ? organizationOwner?._id : activeUser._id;
  const groupsOwnerGroups = isTeamCoach
    ? organizationOwner?.athletesGroups || []
    : activeUser.athletesGroups || [];

  const groupNames = [].concat(req.body.groupNames ?? []);

  // Reorder the stored group objects rather than rebuilding them, so fields
  // like color survive. Reject anything that isn't the same set of groups
  // (e.g. a group was added/removed in another tab since sorting started).
  const groupsByName = new Map(
    groupsOwnerGroups.map((group) => [group.name, group])
  );
  assert(
    groupNames.length === groupsOwnerGroups.length &&
      new Set(groupNames).size === groupNames.length &&
      groupNames.every((name) => groupsByName.has(name)),
    400,
    "Your groups changed, please refresh and try again"
  );

  await api(`user/${groupsOwnerId}`, {
    method: "PUT",
    body: {
      athletesGroups: groupNames.map((name) => groupsByName.get(name)),
    },
  });
  flash("success", "Groups sorted successfully");

  res.refreshWith({}, { url: "back", hash: "#_" });
};

export const deleteGroup = async ({ res, req, locals, flash, api, load }) => {
  const { groupName } = req.body;
  const {
    activeUser: { athletes, _id, athletesGroups },
    organizationOwner,
    organizationRoleName,
  } = locals;

  const isAllowToViewAllOrganizationsAthletes =
    canViewAllOrganizationClients(organizationRoleName);

  // A Team Coach has no groups of their own — the group being deleted lives on
  // the Owner instead (same stand-in used elsewhere for a Team Coach).
  const isTeamCoach = organizationRoleName === "Team Coach";
  const groupsOwnerId = isTeamCoach ? organizationOwner?._id : _id;
  const groupsOwnerGroups = isTeamCoach
    ? organizationOwner?.athletesGroups
    : athletesGroups;

  if (
    process.browser &&
    !confirm("Are you sure you want to delete this group?")
  ) {
    return res.backDontScroll();
  }
  try {
    const updatedAthletesGroups = (groupsOwnerGroups || []).filter(
      (e) => e.name !== groupName
    );
    if (
      locals.activeUser.organizationId &&
      isAllowToViewAllOrganizationsAthletes
    ) {
      await load("allAthletesFromOrganization");

      const allAthletes = parseAthletesOrganizationInfoToGroups(
        locals.allAthletesFromOrganization
      );

      // Object to store the elements by coachId
      const coachIdMap = {};

      allAthletes.forEach((athlete) => {
        const coachId = athlete.coachId;

        // if the property doesn't exists, create it with an empty array
        if (!coachIdMap[coachId]) {
          coachIdMap[coachId] = [];
        }

        // add the athlete to the specific coachId array
        coachIdMap[coachId].push(athlete);
      });

      for (const coachId in coachIdMap) {
        // get the athletes of that specific coach
        const athletesFromThatCoach = coachIdMap[coachId];

        const updatedAthletes = athletesFromThatCoach.map((athlete) => {
          if (athlete.groups) {
            athlete.groups = athlete.groups.filter(
              (group) => groupName !== group
            );
          }
          return filterAthletesAttrs(athlete);
        });

        if (String(groupsOwnerId) === String(coachId)) {
          //update the groups for coach and athlete if it's the group owner
          await api(`user/${coachId}`, {
            method: "PUT",
            body: {
              athletesGroups:
                updatedAthletesGroups.length === 0
                  ? null
                  : updatedAthletesGroups,
              athletes: updatedAthletes,
            },
          });
        } else {
          //update only the groups for the coach
          await api(`user/${coachId}`, {
            method: "PUT",
            body: {
              athletes: updatedAthletes,
            },
          });
        }
      }
    } else {
      const updatedAthletes = athletes.map((athlete) => {
        if (athlete.groups) {
          athlete.groups = athlete.groups.filter(
            (group) => groupName !== group
          );
        }
        return filterAthletesAttrs(athlete);
      });
      await api(`user/${_id}`, {
        method: "PUT",
        body: {
          athletesGroups:
            updatedAthletesGroups.length === 0 ? null : updatedAthletesGroups,
          athletes: updatedAthletes,
        },
      });
    }
    flash("success", "Group deleted");
  } catch (error) {
    console.error(error);
  }

  res.refreshWith(
    { modal: "" },
    { url: "/coaching-portal/athletes", hash: "#_" }
  );
};

export const postUpdateGroupForOrganizations = async ({
  res,
  req,
  locals,
  flash,
  api,
  load,
  assert,
}) => {
  const { name, description, originalGroupName, selected } = req.body;
  const { organizationOwner, organizationRoleName } = locals;

  assert(name, 400, "Please fill out the name field");

  // A Team Coach has no groups of their own — the group being edited lives on
  // the Owner instead (same stand-in used elsewhere for a Team Coach).
  const isTeamCoach = organizationRoleName === "Team Coach";
  const groupsOwnerId = isTeamCoach
    ? organizationOwner?._id
    : locals.activeUser._id;
  const athletesGroups = isTeamCoach
    ? organizationOwner?.athletesGroups || []
    : locals.activeUser.athletesGroups || [];

  const index = athletesGroups.findIndex(
    (elem) => elem.name === originalGroupName
  );
  let nameAlreadyExists = false;
  const groupsWithName = athletesGroups.filter(
    (elem, i) => i !== index && elem.name === name
  );
  if (groupsWithName.length > 0) nameAlreadyExists = true;
  assert(!nameAlreadyExists, 400, "A group with that name already exists.");
  assert(index > -1, 400, "The group to edit could not be found.");
  athletesGroups[index] = { name, description };

  try {
    if (!locals.allAthletesFromOrganization) {
      await load("allAthletesFromOrganization");
    }
    const allAthletes = parseAthletesOrganizationInfoToGroups(
      locals.allAthletesFromOrganization
    );

    // Object to store the elements by coachId
    const coachIdMap = {};

    allAthletes.forEach((athlete) => {
      const coachId = athlete.coachId;

      // if the property doesn't exists, create it with an empty array
      if (!coachIdMap[coachId]) {
        coachIdMap[coachId] = [];
      }

      // add the athlete to the specific coachId array
      coachIdMap[coachId].push(athlete);
    });

    const userUpdatePromises = [];
    for (const coachId in coachIdMap) {
      // get the athletes of that specific coach
      const athletesFromThatCoach = coachIdMap[coachId];

      //this works for editing group names
      const updatedAthletes = athletesFromThatCoach.map((athlete) => {
        if (athlete.groups) {
          athlete.groups = athlete.groups.map((group) => {
            if (group === originalGroupName) {
              return name;
            }
            return group;
          });
        }
        return filterAthletesAttrs(athlete);
      });
      //this is to handle edits on the selected athletes of the group
      updatedAthletes.map((athlete) => {
        const updatedAthlete = athlete;
        const found = selected
          ? selected.find((elem) => elem._id === updatedAthlete._id)
          : null;

        if (found) {
          if (!updatedAthlete.groups?.includes(name)) {
            const groups = found.groups || [];
            updatedAthlete.groups = [...groups, name];
          }
          // delete string if necessary
        } else if (updatedAthlete.groups) {
          updatedAthlete.groups = athlete.groups.filter((e) => e !== name);
        }

        return filterAthletesAttrs(updatedAthlete);
      });
      if (String(groupsOwnerId) === String(coachId)) {
        //update the groups for coach and athlete if it's the group owner
        userUpdatePromises.push(
          api(`user/${coachId}`, {
            method: "PUT",
            body: { athletesGroups, athletes: updatedAthletes },
          })
        );
      }
      //only update the groups for the athletes
      else {
        userUpdatePromises.push(
          api(`user/${coachId}`, {
            method: "PUT",
            body: { athletes: updatedAthletes },
          })
        );
      }
    }

    await Promise.all(userUpdatePromises);

    flash("success", "Group updated");
  } catch (error) {
    console.error(error);
  }

  res.refreshWith(
    { modal: "" },
    { url: `/coaching-portal/athletes/?filterGroup=${name}`, hash: "#_" }
  );
};

export const postUpdateGroup = async ({
  res,
  req,
  locals,
  flash,
  api,
  assert,
}) => {
  const { name, description, originalGroupName, selected } = req.body;
  const { _id, athletes, athletesGroups } = locals.activeUser;

  assert(name, 400, "Please fill out the name field");

  const index = athletesGroups.findIndex(
    (elem) => elem.name === originalGroupName
  );
  let nameAlreadyExists = false;
  const groupsWithName = athletesGroups.filter(
    (elem, i) => i !== index && elem.name === name
  );
  if (groupsWithName.length > 0) nameAlreadyExists = true;
  assert(!nameAlreadyExists, 400, "A group with that name already exists.");
  athletesGroups[index] = { name, description };
  //this works for editing group names

  const updatedAthletes = athletes.map((athlete) => {
    if (athlete.groups) {
      athlete.groups = athlete.groups.map((group) => {
        if (group === originalGroupName) {
          return name;
        }
        return group;
      });
    }
    return filterAthletesAttrs(athlete);
  });
  //this is to handle edits on the selected athletes of the group
  const newAthletes = updatedAthletes.map((athlete) => {
    const updatedAthlete = athlete;
    const found = selected
      ? selected.find((elem) => elem._id === updatedAthlete._id)
      : null;

    if (found) {
      if (!updatedAthlete.groups?.includes(name)) {
        const groups = found.groups || [];
        updatedAthlete.groups = [...groups, name];
      }
      // delete string if necessary
    } else if (updatedAthlete.groups) {
      updatedAthlete.groups = athlete.groups.filter((e) => e !== name);
    }

    return filterAthletesAttrs(updatedAthlete);
  });
  await api(`user/${_id}`, {
    method: "PUT",
    body: { athletesGroups, athletes: newAthletes },
  });
  flash("success", "Group updated");

  res.refreshWith(
    { modal: "" },
    { url: `/coaching-portal/athletes/?filterGroup=${name}`, hash: "#_" }
  );
};

export const postSeedDemo = async ({ res, req, locals, flash, api, load }) => {
  const { athleteId, style } = req.body;

  try {
    const response = await api("seed-demo", {
      method: "POST",
      body: { athleteId, style },
    });

    if (response.status === 200) {
      flash("success", "Demo account seeded successfully");
    } else {
      const result = await response.json().catch(() => ({}));
      flash(
        "error",
        result.error || "There was an error seeding the demo account"
      );
    }
  } catch (e) {
    flash("error", e.message || "There was an error seeding the demo account");
  }

  await load("activeUser", { user: locals.activeUser._id });
  res.refreshWith({}, { url: "/coaching-portal/athletes", hash: "#_" });
};

export const acknowledgeAthlete = async ({ res, req, locals, flash, api }) => {
  const {
    // coach to whom the athlete will be assigned. this only happens with organizations
    coachToId,
    // current athlete coach id. used to edit that user's athletes in db
    coachFromId,
    // athlete to be acknowledged
    athleteId,
    groupNames,
    questionnaireTemplateId,
    femaleCycleTracking,
    schedule,
  } = req.body;

  const { activeUser } = locals;
  const isOrganization = Boolean(activeUser?.organizationId);
  // if coachToId was provided it also means that we are performing the action inside an organization
  // and we have to assign the athlete to the coach that was selected
  const isReassigning =
    isOrganization && coachToId && coachFromId !== coachToId;

  try {
    // reassign first so acknowledge-athlete (and the config/female-cycle-tracking
    // bootstrap it triggers) targets the destination coach's athlete subdoc and
    // default template instead of the source coach's
    if (isReassigning) {
      await api(`organization-re-assign-coach`, {
        method: "POST",
        body: {
          coachToId, // coach to be assigned to
          athleteId,
        },
      });
    }

    // athletes might be coming from 2 sources: if they come from coachFrom,
    // it means that we are treating an organization and we could be
    // handling the athletes from a coach that is not the active user.
    // once reassigned, the athlete subdoc now lives under coachToId
    const coachIdForAthletes = isReassigning
      ? coachToId
      : coachFromId || activeUser?._id;

    // here is where we will be updating the athletes whether from the
    // active user or from the third coach. coachIdForAthletes will
    // determine the coach to be updated
    await api(`acknowledge-athlete`, {
      method: "PUT",
      body: {
        athleteToAcknowledgeId: athleteId,
        // coach that will get the athletes iterated
        coachIdForAthletes,
        groupNames,
        questionnaireTemplateId,
        femaleCycleTracking,
      },
    });

    if (questionnaireTemplateId && schedule) {
      await api(`athlete-questionnaire-schedule`, {
        method: "POST",
        body: {
          schedule,
          athleteId,
          userToRequest: coachToId || coachIdForAthletes,
          userTimeZone: getUserTimeZone(),
        },
      });
    }

    flash("success", "Athlete acknowledged");

    // athlete may now belong to another org coach; profile needs organizationCoach to load it
    const organizationCoach =
      String(coachIdForAthletes) !== String(activeUser?._id)
        ? coachIdForAthletes
        : undefined;

    res.refreshWith(
      {
        athlete: athleteId,
        section: "client-configuration",
        organizationCoach,
        modal: { type: "athlete-acknowledged" },
      },
      { url: `/athlete-portal/profile`, hash: "#_" }
    );
  } catch (error) {
    flash("error", error.message);
    res.refreshWith(
      { modal: "" },
      { url: `/coaching-portal/athletes`, hash: "#_" }
    );
  }
};
